import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toAssetUrl } from "../api/api";
import { fetchActiveCounts } from "../api/articlesApi";
import { fetchCategories } from "../api/categoriesApi";
import CategoryBarChart from "./CategoryBarChart";
import Loader from "./Loader";
import "../styles/Home.css";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({});
  const [totalActive, setTotalActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchCategories(), fetchActiveCounts()])
      .then(([cats, activeCounts]) => {
        if (cancelled) return;
        setCategories(cats);
        setCounts(activeCounts.countsByCategoryId);
        setTotalActive(activeCounts.totalActive);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
            err.message ||
            "Failed to load dashboard data."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const topCategory = categories
    .map((cat) => ({ ...cat, count: counts[cat.id] ?? 0 }))
    .sort((a, b) => b.count - a.count)[0];

  return (
    <div className="home">
      <div className="home-hero">
        <span className="home-eyebrow">Today Talks Admin</span>
        <h2 className="home-heading">Choose a category to get started</h2>
        <p className="home-subheading">
          Manage posts, statuses, and trending flags for each section of the site.
        </p>
      </div>

      <div className="home-stats">
        <div className="home-stat-card">
          <span className="home-stat-label">Total active posts</span>
          <span className="home-stat-value">{loading ? "—" : totalActive}</span>
        </div>
        <div className="home-stat-card">
          <span className="home-stat-label">Categories tracked</span>
          <span className="home-stat-value">{loading ? "—" : categories.length}</span>
        </div>
        <div className="home-stat-card home-stat-card-accent">
          <span className="home-stat-label">Top category</span>
          <span className="home-stat-value home-stat-value-sm">
            {loading || !topCategory
              ? "—"
              : `${topCategory.name} (${topCategory.count})`}
          </span>
        </div>
      </div>

      <div className="home-panel">
        <div className="home-panel-header">
          <h3 className="home-panel-title">Active posts by category</h3>
          <p className="home-panel-subtitle">
            Click any row to jump straight to that category.
          </p>
        </div>

        {loading && <Loader label="Loading dashboard..." />}
        {!loading && error && <p className="home-panel-error">{error}</p>}
        {!loading && !error && categories.length > 0 && (
          <CategoryBarChart categories={categories} counts={counts} />
        )}
        {!loading && !error && categories.length === 0 && (
          <p className="home-panel-error">
            No categories yet. <Link to="/categories">Create one</Link> to get started.
          </p>
        )}
      </div>

      <div className="home-grid">
        {categories.map((cat) => (
          <Link key={cat.id} to={`/posts?category=${cat.id}`} className="home-card">
            {cat.image ? (
              <img className="home-card-img" src={toAssetUrl(cat.image)} alt="" />
            ) : (
              <span className="home-card-icon-fallback" />
            )}
            <span className="home-card-label">{cat.name}</span>
            <span className="home-card-arrow">→</span>
          </Link>
        ))}
      </div>
    </div>
  );
}